import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Globe,
    TrendingUp,
    Shield,
    MapPin,
    DollarSign,
    Calendar,
    Users,
    CheckCircle,
    ArrowRight,
    Star,
    Building2,
    Target,
    Award,
    Heart,
    Sparkles,
    Eye,
    Bookmark,
    MessageCircle,
    Clock,
    BarChart3,
    PieChart,
    Smartphone,
    CreditCard,
    FileText,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { usePageMetadata } from '../../hooks/usePageMetadata';

const RealEstateInvestingFromAbroad = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

    // Set page metadata and canonical URL
    usePageMetadata('realEstateAbroad');

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Key Features
    const features = [
        {
            icon: Globe,
            title: "Global Access",
            description: "Invest in Nigerian real estate from anywhere in the world with our online platform"
        },
        {
            icon: Smartphone,
            title: "Mobile Platform",
            description: "Manage your investments on-the-go with our user-friendly mobile app"
        },
        {
            icon: CreditCard,
            title: "Multiple Currencies",
            description: "Invest in USD, EUR, GBP, or other major currencies with competitive exchange rates"
        },
        {
            icon: FileText,
            title: "Legal Support",
            description: "Complete legal documentation and compliance support for international investors"
        }
    ];

    // Investment Stats
    const stats = [
        { number: "12-20%", label: "Annual ROI", icon: TrendingUp, color: "text-green-600" },
        { number: "2-4 years", label: "Investment Period", icon: Calendar, color: "text-blue-600" },
        { number: "$3,000", label: "Minimum Investment", icon: DollarSign, color: "text-purple-600" },
        { number: "50+", label: "Countries Supported", icon: Globe, color: "text-orange-600" }
    ];

    // Supported Countries
    const supportedCountries = [
        { name: "United States", flag: "🇺🇸", investors: "15,000+" },
        { name: "United Kingdom", flag: "🇬🇧", investors: "12,000+" },
        { name: "Canada", flag: "🇨🇦", investors: "8,000+" },
        { name: "Germany", flag: "🇩🇪", investors: "5,000+" },
        { name: "Australia", flag: "🇦🇺", investors: "4,000+" },
        { name: "France", flag: "🇫🇷", investors: "3,000+" }
    ];

    // Investment Benefits
    const benefits = [
        {
            title: "Remote Management",
            description: "Professional property management team handles all day-to-day operations",
            icon: Users
        },
        {
            title: "Regular Reporting",
            description: "Monthly reports on property performance, rental income, and expenses",
            icon: BarChart3
        },
        {
            title: "Tax Optimization",
            description: "Expert guidance on tax implications and optimization strategies",
            icon: PieChart
        },
        {
            title: "24/7 Support",
            description: "Round-the-clock customer support in multiple languages",
            icon: Clock
        }
    ];

    // Investment Process
    const processSteps = [
        {
            step: "01",
            title: "Account Setup",
            description: "Create your account and complete KYC verification"
        },
        {
            step: "02",
            title: "Browse Properties",
            description: "Explore our curated selection of verified properties"
        },
        {
            step: "03",
            title: "Legal Review",
            description: "Our legal team handles all documentation and compliance"
        },
        {
            step: "04",
            title: "Investment & Management",
            description: "Complete investment and let us manage everything for you"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            {/* Hero Section */}
            <section className="relative pt-20 pb-16 bg-white">
                <div className="container mx-auto px-6 relative z-10">
                    <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Globe className="w-4 h-4" />
                            Real Estate Investing from Abroad
                        </div>
                        
                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Invest in Nigeria
                            <span className="text-blue-600"> from Anywhere</span>
                        </h1>
                        
                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Seamlessly invest in Nigerian real estate from anywhere in the world. 
                            Our global platform makes international real estate investment simple, secure, and profitable.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:-translate-y-1">
                                Start Investing
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <button className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                <Eye className="w-5 h-5" />
                                View Platform
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Global Investment Platform
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our platform is designed specifically for international investors seeking Nigerian real estate opportunities.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                                    <feature.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Supported Countries Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Trusted by Investors Worldwide
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Join thousands of international investors who trust us with their Nigerian real estate investments.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {supportedCountries.map((country, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="text-4xl">{country.flag}</div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">{country.name}</h3>
                                        <p className="text-gray-600">{country.investors} investors</p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        <span className="text-sm text-gray-600">Full platform access</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        <span className="text-sm text-gray-600">Local currency support</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        <span className="text-sm text-gray-600">24/7 customer support</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Choose Our Platform?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            We provide everything you need to successfully invest in Nigerian real estate from abroad.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                                    <benefit.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Investment Process Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Simple Investment Process
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our streamlined process makes international real estate investment simple and secure.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {processSteps.map((step, index) => (
                            <div
                                key={index}
                                className={`text-center transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                            >
                                <div className="relative mb-8">
                                    <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full text-white text-2xl font-bold mb-4">
                                        {step.step}
                                    </div>
                                    {index < processSteps.length - 1 && (
                                        <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-blue-600 -translate-x-1/2"></div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-blue-600">
                <div className="container mx-auto px-6 text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Invest from Abroad?
                        </h2>
                        <p className="text-xl text-green-100 mb-8">
                            Join thousands of international investors who have successfully invested in Nigerian real estate.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-white text-green-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                Start Your Investment
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-transparent text-white px-8 py-4 rounded-xl font-semibold border-2 border-white hover:bg-white hover:text-green-600 transition-all duration-300 hover:-translate-y-1"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Learn More About Real Estate Investing from Abroad Section */}
            <section className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <button
                            onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
                            className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
                            aria-expanded={isLearnMoreOpen}
                            aria-controls="abroad-content"
                        >
                            <span className="font-medium">
                                {isLearnMoreOpen ? 'Hide Learn More About Real Estate Investing from Abroad' : 'Learn More About Real Estate Investing from Abroad'}
                            </span>
                            {isLearnMoreOpen ? (
                                <ChevronUp className="w-4 h-4 text-gray-600" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                        </button>
                    </div>

                    <div
                        id="abroad-content"
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                            }`}
                    >
                        <article className="prose prose-lg max-w-none">
                            {/* Main Content Section */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                    Introduction: Real Estate Investment from Abroad Made Simple
                                </h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For millions of Nigerians in the diaspora, homeownership is not just about money — it's emotional, cultural, and deeply generational. Real estate investment from abroad offers a secure and high-return opportunity to build wealth while staying connected to Nigeria.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    In the past, many Nigerians abroad depended on remittances or relatives to purchase property on their behalf. Unfortunately, this often led to fraud, incomplete projects, or properties that didn't meet expectations. Today, platforms like Diasporawise make it possible for diaspora Nigerians to invest securely in residential, commercial, and fractional properties — all without being physically present in Nigeria.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Real estate from abroad is no longer a dream. It's one of the most practical ways for the diaspora to grow wealth while contributing to the nation's development.
                                </p>
                            </div>

                            {/* Section 2 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">The Growing Importance of Real Estate Investment from Abroad</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Each year, Nigerians abroad send back billions of dollars in remittances, making it one of the largest sources of foreign inflows for the country. However, most remittances are spent on daily expenses like tuition, bills, or healthcare. They don't create long-term wealth.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    This is where foreign real estate investment transforms the game. It allows Nigerians in the diaspora to:
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Build Assets</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Instead of temporary help, investors acquire tangible property.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Protect Wealth</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Real estate in urban Nigeria continues to appreciate in value.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Stay Connected</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Owning property keeps cultural and emotional ties strong.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Contribute to Nation Building</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Construction projects generate jobs, housing, and infrastructure.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    By moving from remittances to structured real estate investments, diaspora Nigerians strengthen their own future while fueling Nigeria's economic growth.
                                </p>
                            </div>

                            {/* Section 3 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Reasons to Invest in Nigerian Real Estate from Abroad</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">1. Huge Housing Demand</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria faces a housing deficit of over 17 million units. Cities like Lagos, Abuja, and Port Harcourt are rapidly urbanizing, making housing investment one of the most secure options for diaspora investors.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">2. Attractive Returns on Investment (ROI)</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Diaspora investors can earn from: Strong rental yields in prime cities. Capital appreciation as property values rise in growing areas. Short-let rentals, popular in Lagos and Abuja among professionals and business travelers.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">3. Diverse Property Options</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    From luxury apartments and gated estates to commercial hubs and land banking projects, there is an investment category for every budget.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">4. Secure Digital Platforms</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Modern platforms like Diasporawise ensure: Vetted, compliant property deals. Digital dashboards to monitor investments. Title verification and reporting. Safe and transparent payment channels.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">5. Government Incentives</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The Nigerian government supports diaspora real estate investment through policies, partnerships, and housing initiatives tailored to Nigerians abroad.
                                </p>
                            </div>

                            {/* Section 4 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Investment Categories Available from Abroad</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Residential Property Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Diaspora Nigerians can invest in: Gated communities. Apartment complexes. Short-let apartments. Homes for Nigeria's expanding middle class.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    This provides steady rental returns while helping address the housing shortage.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Commercial Property Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Lagos, Abuja, and Port Harcourt are booming commercial centers. Nigerians abroad can invest in: Office complexes. Shopping malls. Mixed-use developments.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Commercial real estate typically generates higher ROI but often requires larger capital.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Land Banking and Development</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Buying land in fast-growing urban areas remains one of the safest and most profitable entry points. Diaspora investors can now complete secure land purchases online, with verified titles and proper documentation.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fractional Real Estate Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Not every investor has a large budget. Fractional investment enables diaspora Nigerians to own shares in premium projects. Benefits include: Lower entry costs. Shared rental income. Access to high-value developments.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    This model democratizes real estate investment for Nigerians abroad.
                                </p>
                            </div>

                            {/* Section 5 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits of Investing Through Diasporawise</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For Nigerians in the diaspora, Diasporawise is a trusted gateway into Nigeria's real estate market.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Key benefits include:</h3>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Security & Compliance</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Projects undergo strict legal checks.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Transparency</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Regular reports and digital investment tracking.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Flexibility</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Start small with fractional ownership or scale up to full projects.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Impact</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investments generate jobs, homes, and infrastructure.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural Connection</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Property investments reconnect families with their homeland.
                                </p>
                            </div>

                            {/* Section 6 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Steps to Start Real Estate Investment from Abroad</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Set Clear Objectives</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Decide if your goal is rental income, capital gains, or fractional ownership.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Pick a Trusted Platform</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Choose compliant platforms like Diasporawise.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Select an Investment Category</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Residential, commercial, land, or fractional.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Verify Legal Documents</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Ensure all land titles and permits are legitimate.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Use Secure Payment Channels</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Avoid cash transactions; opt for verified digital systems.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Monitor Your Investments</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Use dashboards for real-time updates.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Build Wealth Over Time</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Watch your investments grow while contributing to Nigeria's progress.
                                </p>
                            </div>

                            {/* Section 7 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Challenges and How to Overcome Them</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    While rewarding, investing from abroad comes with challenges:
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fraudulent Deals</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Avoid by using vetted platforms only.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Unverified Land Titles</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Always insist on verified digital title checks.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Management Issues</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Engage professional property managers.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Currency Fluctuations</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Plan around exchange rate changes when sending funds.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Structured platforms and trusted partners help diaspora Nigerians overcome these risks effectively.
                                </p>
                            </div>

                            {/* Conclusion */}
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Building Wealth and Roots from Afar</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Real estate investment from abroad is no longer risky or uncertain. It is structured, transparent, and highly profitable. Nigerians in the diaspora can now confidently own residential estates, commercial projects, land, or fractional shares in premium properties.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Beyond financial returns, property investment offers cultural connection, wealth preservation, and a role in Nigeria's development.
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    With Diasporawise, owning property in Nigeria from anywhere in the world has never been easier — or safer.
                                </p>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default RealEstateInvestingFromAbroad;
